import { Button, Dropdown, Layout, Menu, Space, Spin, Tabs } from 'antd';
import { KeyOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import type { ItemType } from 'antd/es/menu/interface';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { TOP_NAVS, type TopNavId } from './navigation';
import { sidebarFor, type PanelDef } from '../panels/registry';
import { useAuth } from '../auth/AuthContext';
import { canSee } from '../auth/roles';
import { visibleSidebar } from '../auth/panelAccess';

function buildExternalUrlMap(panels: PanelDef[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const panel of panels) {
    if (panel.externalUrl) map[panel.id] = panel.externalUrl;
  }
  return map;
}
import { StatusBar } from '../components/StatusBar';
import BtcHeaderTicker from '../components/BtcHeaderTicker';

const { Header, Sider, Content, Footer } = Layout;

/// Build the sidebar menu — flat for ungrouped panels, nested under a
/// SubMenu when panels share a `group`. Group order follows first
/// appearance; ungrouped panels keep their relative position so that
/// e.g. "实时盘口" can sit above "行情概览" / "逐笔成交".
function buildSidebarItems(panels: PanelDef[]): {
  items: ItemType[];
  groupKeyByPanel: Record<string, string>;
} {
  const items: ItemType[] = [];
  const groupKeyByPanel: Record<string, string> = {};
  const groupIndexInItems = new Map<string, number>();

  for (const panel of panels) {
    const child: ItemType = { key: panel.id, label: panel.title };
    if (!panel.group) {
      items.push(child);
      continue;
    }
    groupKeyByPanel[panel.id] = panel.group.id;
    const existing = groupIndexInItems.get(panel.group.id);
    if (existing == null) {
      groupIndexInItems.set(panel.group.id, items.length);
      items.push({
        key: panel.group.id,
        label: panel.group.title,
        children: [child],
      });
    } else {
      // SubMenu items have a `children` array; push the new entry into it.
      const sub = items[existing] as { children: ItemType[] };
      sub.children.push(child);
    }
  }

  return { items, groupKeyByPanel };
}

export function ShellLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, logout, ready } = useAuth();

  // Wait for the role before rendering nav/routes so we don't flash the
  // wrong section while a persisted token is being validated on boot.
  if (!ready) {
    return (
      <Layout
        style={{
          height: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="large" />
      </Layout>
    );
  }

  const segments = location.pathname.split('/').filter(Boolean);
  const activeTopNav = (TOP_NAVS.find((n) => n.id === segments[0])?.id ??
    'home') as TopNavId;
  const activeSidebar = segments[1] ?? '';

  // 面板按角色过滤:requiresWs(排除 user)+ 可选的 per-panel roles(如 trader
  // 里 Quant/Maker 两组各自的角色隔离)。
  const panels = visibleSidebar(sidebarFor(activeTopNav), role);
  const { items: sidebarItems, groupKeyByPanel } = buildSidebarItems(panels);
  const externalUrlByKey = buildExternalUrlMap(panels);
  const activeGroupKey = activeSidebar ? groupKeyByPanel[activeSidebar] : undefined;

  return (
    <Layout style={{ height: '100vh' }}>
      <Header
        style={{
          background: '#ffffff',
          padding: '0 24px',
          borderBottom: '1px solid #eaeaea',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div style={{ flex: 1, fontWeight: 600 }}>carrier</div>
        <Tabs
          activeKey={activeTopNav}
          onChange={(key) => navigate(`/${key}`)}
          items={TOP_NAVS.filter((n) => canSee(role, n.id)).map((n) => ({
            key: n.id,
            label: n.label,
          }))}
        />
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <BtcHeaderTicker />
          <Dropdown
            menu={{
              items: [
                {
                  key: 'change-password',
                  icon: <KeyOutlined />,
                  label: '修改密码',
                  onClick: () => navigate('/account/password'),
                },
                { type: 'divider' },
                {
                  key: 'logout',
                  icon: <LogoutOutlined />,
                  label: '退出登录',
                  onClick: () => {
                    logout();
                    navigate('/login', { replace: true });
                  },
                },
              ],
            }}
            placement="bottomRight"
          >
            <Button type="text" icon={<UserOutlined />} style={{ marginLeft: 12 }}>
              <Space size={4}>{user?.name ?? '账户'}</Space>
            </Button>
          </Dropdown>
        </div>
      </Header>
      {/* `minHeight: 0` + `overflow: hidden` lets the middle row shrink
          below its content size — without it, a long Sider menu (e.g. the
          12 美元指标 items) pushes the Footer status bar out of the
          viewport. The Sider scrolls internally instead. */}
      <Layout style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* A single-panel section (e.g. Tweet) has nothing to navigate —
            skip the Sider and give the panel the full width. */}
        {panels.length > 1 && (
          <Sider
            // 与单 panel 页面(Radar/分形演化论等)内部第一列的 16.7% 对齐,
            // 让所有 section 的第一列视觉宽度一致。
            width="16.7%"
            theme="light"
            style={{
              borderRight: '1px solid #eaeaea',
              overflowY: 'auto',
              height: '100%',
            }}
          >
            <Menu
              mode="inline"
              selectedKeys={activeSidebar ? [activeSidebar] : []}
              defaultOpenKeys={activeGroupKey ? [activeGroupKey] : []}
              items={sidebarItems}
              onClick={({ key }) => {
                const url = externalUrlByKey[key];
                if (url) {
                  window.open(url, '_blank', 'noopener,noreferrer');
                  return;
                }
                navigate(`/${activeTopNav}/${key}`);
              }}
              style={{ borderRight: 0 }}
            />
          </Sider>
        )}
        <Content
          style={{
            padding: 0,
            margin: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          <Outlet />
        </Content>
      </Layout>
      <Footer
        style={{
          padding: '6px 16px',
          background: '#ffffff',
          borderTop: '1px solid #eaeaea',
        }}
      >
        <StatusBar />
      </Footer>
    </Layout>
  );
}
